import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Receipt } from 'lucide-react';
import { toast } from 'sonner';

import api from '../services/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Textarea } from './ui/textarea';


const OPEN_STATUSES = new Set(['sent', 'partial_paid', 'overdue']);


function money(value, currency = 'CAD') {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value || 0));
}


function date(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
}


export default function OpenInvoicesEmailDialog({ open, onOpenChange, scopeType, scope }) {
  const client = scopeType === 'client' ? scope : scope?.client;
  const [invoices, setInvoices] = useState([]);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !scope?.id) return;
    setRecipientEmail(client?.email || '');
    setMessage('');
    setLoading(true);
    const queryName = scopeType === 'client' ? 'client_id' : 'project_id';
    api.get(`/invoices?${queryName}=${scope.id}&exclude_paid=true`)
      .then((response) => {
        setInvoices((response.data || []).filter(
          (invoice) => OPEN_STATUSES.has(invoice.status) && Number(invoice.balance_due || 0) > 0
        ));
      })
      .catch(() => {
        setInvoices([]);
        toast.error('Unable to load open invoices.');
      })
      .finally(() => setLoading(false));
  }, [open, scope?.id, scopeType, client?.email]);

  const totals = useMemo(() => invoices.reduce((result, invoice) => {
    const currency = (invoice.currency || 'CAD').toUpperCase();
    result[currency] = (result[currency] || 0) + Number(invoice.balance_due || 0);
    return result;
  }, {}), [invoices]);

  const send = async () => {
    if (!recipientEmail.trim()) {
      toast.error('A recipient email address is required.');
      return;
    }
    if (invoices.length === 0) return;
    setSending(true);
    try {
      await api.post('/invoices/open-summary/send', {
        scope_type: scopeType,
        scope_id: scope.id,
        recipient_email: recipientEmail.trim(),
        recipient_name: client?.contact_person || client?.primary_contact_name || client?.name || '',
        message,
      });
      toast.success(`Open invoice summary emailed to ${recipientEmail.trim()}.`);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Open invoice summary could not be sent.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !sending && onOpenChange(nextOpen)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-blue-600" /> Open invoices — {scope?.name || ''}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-500">Loading open invoices…</div>
        ) : invoices.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-600">
            There are no open issued invoices for this {scopeType}.
          </div>
        ) : (
          <div className="space-y-5">
            <div className="max-h-[320px] overflow-auto rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Due date</TableHead>
                    <TableHead className="text-right">Amount due</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                      <TableCell>{date(invoice.due_date)}</TableCell>
                      <TableCell className="text-right font-medium">{money(invoice.balance_due, invoice.currency || 'CAD')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-wrap justify-end gap-3 text-lg font-semibold">
              <span>Total outstanding:</span>
              {Object.entries(totals).map(([currency, amount]) => <span key={currency}>{money(amount, currency)}</span>)}
            </div>

            <div className="space-y-2">
              <Label>Send to</Label>
              <Input type="email" value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} placeholder="client@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Optional message</Label>
              <Textarea rows={4} value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Add a short note for the client…" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={sending} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={loading || sending || invoices.length === 0} onClick={send}>
            <Mail className="mr-2 h-4 w-4" /> {sending ? 'Sending…' : `Email ${invoices.length} open invoice${invoices.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
